import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
  InputAdornment,
  IconButton,
  Alert,
  Stack,
} from "@mui/material";
import {
  Login as LoginIcon,
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Info as InfoIcon,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import { validateEmail } from "../../utils/formValidation";
import Logo from "../../components/Logo";
import AuthBackgroundSlideshow from "../../components/AuthBackground/Slideshow";

/**
 * Modern Sign In Page with Material UI
 * Features:
 * - Clean, centered card layout
 * - Password visibility toggle
 * - Remember me checkbox
 * - Email validation
 * - Deadline status indicator
 * - Responsive design
 * - Animated background with slow zoom effect
 */
export default function SignIn() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDeadline = async () => {
      try {
        const response = await myApi.get("/deadline");
        if (response.data.result) {
          const deadlineInfo = response.data.result;
          const dateNow = new Date();
          const dateLimit = new Date(deadlineInfo.general_deadline);
          setIsDeadlinePassed(dateNow > dateLimit);
        }
      } catch (error) {
        console.error("Error fetching deadline:", error);
        setIsDeadlinePassed(false);
      }
    };

    fetchDeadline();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const formData = new FormData(event.currentTarget);
    const email = formData.get("email");

    // Validate email format
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      toast(emailValidation.message, { type: "error" });
      setEmailError(emailValidation.message);
      setLoading(false);
      return;
    }
    setEmailError("");

    const data = {
      username: email,
      passwd: formData.get("password"),
    };

    try {
      const response = await myApi.post("/auth", data);

      // Check if login was successful
      if (response.data.status === 'success' && response.data.result) {
        // Save token and user data
        myApi.setToken(response.data.result.token);
        myApi.setUser(response.data.result);
        
        // Show success notification
        toast.success("Login successful! Redirecting...", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // Check if user is active
        if (response.data.result.user_status !== "active") {
          toast.warning(
            "User belum diaktivasi. Anda akan diarahkan ke halaman aktivasi.",
            {
              position: "top-right",
              autoClose: 3000,
            }
          );
          setTimeout(() => {
            const inactiveUserData = {
              email: response.data.result.email,
              name: response.data.result.name,
              user_status: response.data.result.user_status,
            };
            navigate("/auth/resend-activation/", {
              state: { data: inactiveUserData },
            });
          }, 1500);
        } else {
          // Redirect based on role
          setTimeout(() => {
            if (response.data.result.role === "admin") {
              window.location.href = "/dashboard/";
            } else {
              window.location.href = "/paper/list/";
            }
          }, 1500);
        }
      } else {
        // Handle case where response doesn't have expected data
        const errorMessage = response.data?.message || 'Login failed. Please try again.';
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // Handle different error scenarios
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
        
        // Handle specific status codes
        if (error.response.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.message || 'Please fill in all fields';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      }
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    if (emailError) {
      const validation = validateEmail(e.target.value);
      setEmailError(validation.valid ? "" : validation.message);
    }
  };

  return (
    <>
      {/* Animated Background Slideshow */}
      <AuthBackgroundSlideshow />
      <Container maxWidth="sm" sx={{ minHeight: "100vh", py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Logo size={100} type="horizontal" />
        </Box>

        {/* Welcome Text */}
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Welcome Back!
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Sign in to continue to KARSA NUSA
        </Typography>

        {/* Deadline Alert */}
        {isDeadlinePassed !== null && (
          <Alert
            severity={isDeadlinePassed ? "error" : "info"}
            sx={{ mb: 3 }}
            icon={<InfoIcon />}
          >
            {isDeadlinePassed
              ? "Paper Submission registration is closed."
              : "Paper submission is still open."}
          </Alert>
        )}

        {/* Sign In Form */}
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Email Field */}
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              placeholder="email@example.com"
              required
              error={!!emailError}
              helperText={emailError}
              onChange={handleEmailChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Remember Me & Forgot Password */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    color="primary"
                  />
                }
                label="Remember me"
              />
              <MuiLink
                component={Link}
                to="/auth/forgot-password/"
                underline="hover"
                fontWeight={500}
              >
                Forgot Password?
              </MuiLink>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={<LoginIcon />}
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                mt: 1,
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            {/* Sign Up Link */}
            <Typography variant="body2" textAlign="center" mt={2}>
              Don't have an account?{" "}
              <MuiLink
                component={Link}
                to="/auth/sign-up/"
                underline="hover"
                fontWeight={600}
              >
                Create Account
              </MuiLink>
            </Typography>
          </Stack>
        </form>

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © 2026 KARSA NUSA. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
    </>
  );
}
